//
// # RecoverSessionDialog
//
// Command-palette style prompt shown when opening a design that has an
// orphaned (editor-less) unsaved in-memory session on the server — e.g. after
// reloading the window mid-edit. Lets the user recover those unsaved changes
// or discard them and open the last saved version from depot.
//

import React, { useCallback } from "react";
import { createPortal } from "react-dom";

import { useWorkspace, useWorkspaceSnapshot } from "../context";

export interface RecoverSessionDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function RecoverSessionDialog({
  open,
  onClose,
}: RecoverSessionDialogProps) {
  const workspace = useWorkspace();
  const { recoverSessionDialog } = useWorkspaceSnapshot();

  const handleRecover = useCallback(() => {
    if (!recoverSessionDialog) return;
    workspace.recoverUnsavedSession(recoverSessionDialog.ident);
  }, [recoverSessionDialog, workspace]);

  const handleOpenSaved = useCallback(() => {
    if (!recoverSessionDialog) return;
    void workspace.openSavedVersion(recoverSessionDialog.ident);
  }, [recoverSessionDialog, workspace]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleRecover();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [handleRecover, onClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!open || !recoverSessionDialog) return null;

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">Unsaved changes</div>
        <div className="add-instance-hint">
          {recoverSessionDialog.displayName} has unsaved changes from a previous
          session. Recover them, or open the last saved version?
        </div>
        <div className="add-instance-footer">
          <span className="add-instance-hint">
            Enter to recover · Escape to cancel
          </span>
          <button className="add-instance-btn" onClick={handleOpenSaved}>
            Open saved version
          </button>
          <button
            className="add-instance-btn"
            onClick={handleRecover}
            autoFocus
          >
            Recover unsaved changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
